import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import contextSectionNavigation from '@contexts/sectionNavigation'
import Blink from '@components/Blink'
import {
  SectionProps,
  SectionWithLink,
  SectionWithNoLink,
} from '@widgets/SectionNavigation/types'
import { desktopCSS, mobileCSS, tabletCSS } from '@measures/responsive'
import Subsection from './Subsection'
import { ellipsis } from 'polished'
import useSectionTracking from '@hooks/useSectionTracking'

const { SectionNavigationContext } = contextSectionNavigation

interface StyledSectionNavigationProps {
  isVideo: boolean
}

const StyledSectionNavigation = styled.div<StyledSectionNavigationProps>`
  ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_60 },
        grey: { grey_10 },
      },
    },
    isVideo,
  }) => {
    return css`
      position: relative;
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      ${isVideo && `background-color: ${btvGrey_60}; color: ${grey_10};`}

      ${mobileCSS(css`
        padding: 16px 0 0 10px;
      `)}

      ${tabletCSS(css`
        margin: 0 -52px;
        padding: 16px 52px 0 52px;
      `)}

      ${desktopCSS(css`
        margin: 0 -20px;
        padding: 16px 20px 0 20px;
      `)}
    `
  }}
`

const StyledNav = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  overflow: hidden;
  position: relative;
  margin-bottom: 4px;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none;
  }
`

const ScrollWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-end;
  overflow-x: auto;
  overflow-y: hidden;
  flex-grow: 1;
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none;
  }
`

const Subsections = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-grow: 1;
  align-items: flex-end;
  padding-right: 40px;
`

type StyledSectionProps = StyledSectionNavigationProps

const sectionNameStyle = (isVideo: boolean) => css`
  ${({
    theme: {
      fonts: { goodOTNarrow },
      colors: {
        white,
        grey: { grey_100 },
      },
    },
  }) => {
    return `
    flex-shrink: 0;
    min-width: 65px;
    margin-right: 8px;
    display: flex;
    align-items: flex-end;
    line-height: 42px;
    font-size: 40px;
    font-family: ${goodOTNarrow};
    font-weight: 900;
    text-transform: uppercase;
    color: ${isVideo ? white : grey_100};
`
  }}
`

const StyledClickableSectionName = styled(Blink)<StyledSectionProps>`
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
    },
    isVideo,
  }) => {
    return css`
      ${sectionNameStyle(isVideo)}
      text-decoration: none;
      ${ellipsis('100%', 1)};
      &:hover {
        color: ${brand_55};
      }
    `
  }}
`

const StyledSectionName = styled.div<StyledSectionProps>`
  ${({ isVideo }) => {
    return css`
      ${sectionNameStyle(isVideo)}
      ${ellipsis('100%', 1)};
    `
  }}
`

interface FadeEffectProps {
  isVideo: boolean
  isRight?: boolean
  displayFadeEffect: boolean
}

const FadeEffect = styled.div<FadeEffectProps>`
  ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_60 },
        white,
      },
    },
    isVideo,
    isRight,
    displayFadeEffect,
  }) => {
    if (!displayFadeEffect)
      return css`
        display: none;
      `
    return css`
      position: absolute;
      height: 100%;
      background: linear-gradient(
        ${isRight ? 'to right' : 'to left'},
        ${isVideo ? 'rgba(46, 54, 61, 0)' : 'rgba(255, 255, 255, 0)'},
        ${isVideo ? btvGrey_60 : white}
      );
      top: 0;
      ${isRight
        ? css`
            width: 40px;
            right: 0;
          `
        : css`
            width: 10px;
            left: 0;
          `}
    `
  }}
`

const TheUltimateFaker = styled.div`
  ${({
    theme: {
      colors: {
        blicktvGrey: { btvGrey_60 },
      },
    },
  }) => {
    return css`
      position: absolute;
      width: 100%;
      height: 20px;
      top: 100%;
      left: 0;
      background-color: ${btvGrey_60};
    `
  }}
`

const isClickableSection = (section: any): section is SectionWithNoLink =>
  !!section.link

const Section: FunctionComponent<SectionProps> = ({ section, subsections }) => {
  const { isVideoBlock } = useContext(SectionNavigationContext)
  const { name } = section

  const displayFadeEffect = !!subsections

  const sectionTracking = useSectionTracking(name)

  return (
    <StyledSectionNavigation
      isVideo={isVideoBlock}
      ref={sectionTracking.viewportRef}>
      {isClickableSection(section) ? (
        <StyledClickableSectionName
          {...(section as SectionWithLink).link}
          isVideo={isVideoBlock}
          onClick={sectionTracking.onClick}>
          {name}
        </StyledClickableSectionName>
      ) : (
        <StyledSectionName isVideo={isVideoBlock}>{name}</StyledSectionName>
      )}
      <StyledNav>
        <FadeEffect
          isVideo={isVideoBlock}
          displayFadeEffect={displayFadeEffect}
        />
        <ScrollWrapper>
          <Subsections>
            {subsections &&
              subsections.map((subsection, index) => (
                <Subsection key={`${subsection.name}-${index}`}>
                  {subsection}
                </Subsection>
              ))}
          </Subsections>
        </ScrollWrapper>
      </StyledNav>
      <FadeEffect
        isRight
        isVideo={isVideoBlock}
        displayFadeEffect={displayFadeEffect}
      />
      {/* This is needed to avoid to see white background between section navigation and layout multiline */}
      {isVideoBlock ? <TheUltimateFaker /> : null}
    </StyledSectionNavigation>
  )
}

export default Section
